import {EventEmitter, Injectable} from '@angular/core';
import {TorchAPIConfiguration} from '../../../config/api.configuration';
import {HttpClient} from '@angular/common/http';
import {Feedback} from '../models';
import {Burly} from 'kb-burly';
import html2canvas from 'html2canvas';
import {BehaviorSubject, Observable} from 'rxjs';
import {switchMap, tap} from 'rxjs/operators';
import {Angulartics2} from 'angulartics2';
import {RequestQueryBuilder, SCondition} from '@nestjsx/crud-request';
import {TypedPaginationResponse} from '../../../responses';
import {TorchBadgeUpdatedListener} from '../../../../app/shared/services/torch-navigation.service';
import {TorchFilesService} from '../../@files';

@Injectable({
  providedIn: 'root'
})
export class TorchFeedbackService {
  static feedbackTypes: { name: string; desc: string; icon: string }[] = [
    {
      name: 'Bug Report',
      desc: 'Used to submit errors and bugs found in TORCH',
      icon: 'report_problem'
    },
    {
      name: 'Enhancement Request',
      desc: 'Used to request new features, modules, functions, or enhancements to TORCH',
      icon: 'new_releases'
    },
    {
      name: 'General Feedback',
      desc: 'Used to ask questions about TORCH or talk in general about the application',
      icon: 'record_voice_over'
    },
    {
      name: 'Access Request',
      desc: 'Used to request access to a screen you believe you should have access to but do not',
      icon: 'visibility_off'
    }
  ];

  currentScreenshot: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  feedbackCreated: EventEmitter<boolean> = new EventEmitter<boolean>();

  // Markdown template for submitting feedback
  private feedbackTemplate = `
  <b>Type</b>: $TYPE<br>
  <b>Submitted By</b>: <a href="mailto:$USERNAME">$USERNAME</a><br>
  <b>URL</b>: <a href="$URL">$URL</a><br><br>
  <b>Message</b>:<br>
  $MESSAGE
  `;

  private badgeUpdatedListeners: TorchBadgeUpdatedListener[] = [];

  constructor(protected config: TorchAPIConfiguration,
              private httpClient: HttpClient,
              private angulartics2: Angulartics2,
              private filesService: TorchFilesService) {
  }

  public submitFeedbackItem(feedback: Feedback) {
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/feedback')
      .get;

    this.angulartics2.eventTrack.next({
      action: 'Create Feedback Item',
      properties: {
        category: feedback.type
      }
    });

    return this.httpClient.post<Feedback>(endpointURL, feedback).pipe(
      tap(() => {
        this.currentScreenshot.next(null);
        this.feedbackCreated.emit(true);
        this.updateSidebarBadge();
      })
    );
  }

  public getCount() {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/feedback')
      .addSegment('/count')
      .get;

    return this.httpClient.get<number>(endpoint);
  }

  public createWorkItem(feedback: Feedback) {
    let feedbackMessage = `${feedback.description}<br><img src="${this.getScreenshotURL(feedback)}" alt="feedback-screenshot">`;
    feedbackMessage = `${feedbackMessage}<br><b>Logs:</b><br><pre>${feedback.logs.split('\n').join('<br>')}</pre>`;

    const feedbackContent = this.feedbackTemplate
      .replace(/\$TYPE/g, feedback.type)
      .replace(/\$USERNAME/g, feedback.submittedBy.username)
      .replace(/\$URL/g, feedback.url)
      .replace(/\$MESSAGE/g, feedbackMessage);

    const body = {
      title: `${feedback.type} - ${feedback.title}`,
      description: feedbackContent,
      type: 'User Story'
    };

    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/devOps')
      .addSegment('/createWorkItem')
      .get;

    this.angulartics2.eventTrack.next({
      action: 'Create Feedback Item',
      properties: {
        category: feedback.type
      }
    });

    return this.httpClient.post<{ id: number } | null>(endpointURL, body).pipe(
      switchMap((response) => {
        feedback.workItemCreated = true;
        feedback.workItemID = response.id;
        return this.updateFeedbackItem(feedback);
      })
    );
  }

  public deleteFeedbackItem(feedback: Feedback) {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/feedback')
      .addSegment(`/${feedback.id}`)
      .get;

    return this.httpClient.delete(endpoint).pipe(
      tap(() => this.updateSidebarBadge())
    );
  }

  public updateFeedbackItem(feedback: Feedback): Observable<Feedback> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/feedback')
      .addSegment(`/${feedback.id}`)
      .get;

    return this.httpClient.patch<Feedback>(endpoint, feedback).pipe(
      tap(() => this.updateSidebarBadge())
    );
  }

  public getFeedbackItems(pageSize: number = 50,
                          currentPage: number = 0,
                          sortDirection: 'ASC' | 'DESC' | '' = 'DESC',
                          sortBy: string = 'id',
                          filters: SCondition | null): Observable<TypedPaginationResponse<Feedback>> {
    const qb = RequestQueryBuilder.create();

    qb.setPage(currentPage + 1).setLimit(pageSize);

    if (!!sortDirection) {
      qb.sortBy({field: sortBy, order: sortDirection});
    }

    if (!!filters) {
      qb.search(filters);
    }

    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/feedback')
      .appendRawQueryString(qb.query(false))
      .get;

    return this.httpClient.get<TypedPaginationResponse<Feedback>>(endpoint);
  }

  /**
   * Send an email to admins with feedback content
   */
  public sendUserFeedbackEmail(feedback: Feedback) {
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/email/newUserFeedbackMessage')
      .get;

    return this.httpClient.post(endpointURL, {
      feedbackSubmittedBy: feedback.submittedBy.enteredBy,
      feedbackCategory: feedback.type,
      feedbackTitle: feedback.title,
      submittedFeedback: feedback.description
    });
  }

  public takeScreenshot() {
    html2canvas(document.body as HTMLElement).then(canvas => {
      this.currentScreenshot.next(canvas.toDataURL('image/png'));
    });
  }

  public addBadgeUpdatedListener(listener: TorchBadgeUpdatedListener) {
    if (!this.badgeUpdatedListeners.includes(listener)) {
      this.badgeUpdatedListeners.push(listener);
    }
  }

  private updateSidebarBadge() {
    this.getCount().subscribe(count => {
      this.badgeUpdatedListeners.forEach(listener => {
        listener.badgeUpdated('user-feedback', count, false);
      });
    });
  }

  private getScreenshotURL(feedback: Feedback): string {
    const fileName = feedback.screenshot.split('/').pop();
    return this.filesService.getURL(fileName);
  }
}
